import {get, map, find, isString, isEmpty, isNil, isArray,
    assign, pick, omit, forIn, isNumber, every, isEqual,
    merge} from 'lodash';
import {useState, useCallback, useMemo, useRef, useContext, useEffect} from 'react';
import * as tmp from 'react-router-dom';
import {getColumnKey, getColumnPos} from 'acud/lib/table/util';

import useTableFetch from './fetch';
import useCommand from './command';
import {TGetTableOptions} from './interface';

import {useLocale} from '../../../components/locale';
import {ConfigContext} from '../../../components/common/ConfigProvider';
import useSearchParams from '../../common/searchParams';
import Enum from '../../../utils/Enum';

const DEFAULT_PAGE_NO = 1;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGESIZE_OPTIONS = ['10', '20', '50', '100'];
const DEFAULT_PAGE_PARAMS = ['pageNo', 'pageSize'];
const DEFAULT_QUERY_PARAMS = [...DEFAULT_PAGE_PARAMS, 'orderBy', 'order'];

// ignore waring `"export 'useNavigate' (imported as 'rc') was not found in 'react-router-dom'`
const rc = tmp as any;

interface SearchParams {
    pageNo?: number;
    pageSize?: number;
    orderBy?: string;
    order?: string;
}

interface DefaultFilterData {
    order?: string;
    orderBy?: string;
}

const TABLE_ORDER_MAP = new Enum(
    {alias: 'ascend', value: 'asc'},
    {alias: 'descend', value: 'desc'}
);

export const FETCH_TRIGGER_BY = new Enum(
    {alias: 'Initial', value: 'initial', text: '默认'},
    {alias: 'Filter', value: 'filter', text: '过滤'},
    {alias: 'Pagination', value: 'pagination', text: '翻页'},
    {alias: 'Reload', value: 'reload', text: '重载'},
    {alias: 'Polling', value: 'polling', text: '轮询'}
);

export const FETCH_TRIGGER_BY_RELOAD_TYPE = new Enum(
    {alias: 'Auto', value: 'auto', text: '自动'},
    {alias: 'Manually', value: 'manually', text: '手动'},
);

const getSearch = location => {
    const {hash, search} = location;
    return (hash || search)?.split('?')[1];
};

const getUrl = (location, searchParamsString) => {
    const {pathname, hash} = location;
    let search = `?${searchParamsString}`;
    if (hash) {
        search = hash.indexOf('?') > -1 ? hash.replace(/\?.*/, () => search) : hash + search;
    }
    return pathname + search;
};

const useTable = (getOptions: TGetTableOptions, props?: any, customFunctions: any = {}) => {

    const {actions, history: propsHistory, search: propsSearch} = props || {};

    const hooksHistory = rc.useHistory?.();
    const navigate = rc.useNavigate?.();
    const hooksLocation = rc.useLocation?.();

    const history = propsHistory || hooksHistory || navigate;
    const location = history?.location || hooksLocation;
    const search = propsSearch || getSearch(location);

    const {getSearchParam, searchParamsInstance} = useSearchParams(search);

    const {queryParamKeys: tableQueryParamKeys, fetchActionErrorToast} = useContext(ConfigContext)?.table || {};

    const callbackRef = useRef<any>({
        tableActions: {
            getSearchParam
        }
    });

    const [selectedRow, setSelectedRow] = useState({selectedRowKeys: [], selectedItems: []});

    const {dialogProps, handleCommand} = useCommand({actions, history, selectedRow, ...callbackRef.current});

    const [locale] = useLocale('useTable');

    // 表格中事件处理
    const handleTableCommand = useCallback((type: string, payload?: any) => {
        const {tableOptions} = callbackRef.current;
        const commandConfig = get(tableOptions, `commands[${type}]`);
        commandConfig && handleCommand({
            config: commandConfig,
            payload
        });
    }, [handleCommand]);

    const tableOptions = getOptions(
        props || {},
        handleTableCommand,
        {...callbackRef.current.tableActions, ...customFunctions}
    );

    const {
        className,
        title,
        // 请求相关配置
        fetchListAction,
        $onRequest,
        $onResponse,
        $onError,
        errorToast = fetchActionErrorToast ?? true,
        abort,
        interval,
        queryParamKeys: defaultQueryParamKeys = (tableQueryParamKeys ?? false),
        $onQueryParamsChange,
        // 批量操作相关配置
        bulkConfig,
        bulkComp,
        // bulkPosition,
        // 筛选区域相关配置
        filterConfig: defaultFilterConfig,
        filterComp,
        // 表格相关配置
        rowKey,
        select = 'multi',
        pageNo: defaultPageNo,
        pageSize: defaultPageSize,
        pageSizeOptions,
        tableConfig,
        tableComp,
        card,
        emptyConfig,
        emptyComp
        // 表格操作相关配置
        // commands

    } = tableOptions;

    const queryParamKeys = useMemo(() => {
        if (!defaultQueryParamKeys) {
            return [];
        }
        return (defaultQueryParamKeys === true ? DEFAULT_QUERY_PARAMS : defaultQueryParamKeys) as string[];
    }, [defaultQueryParamKeys]);

    const getQueryParamValue = useCallback(paramName => {
        const searchParams = getSearchParam() as SearchParams;
        return queryParamKeys.includes(paramName) ? searchParams?.[paramName] : undefined;
    }, [queryParamKeys, getSearchParam]);

    const getCustomQueryParamsValue = useCallback(() => {
        const searchParams = getSearchParam() as SearchParams;
        return omit(pick(searchParams, queryParamKeys), DEFAULT_PAGE_PARAMS);
    }, [queryParamKeys, getSearchParam]);

    const {columns, filterConfig, defaultFilterData} = useMemo(() => {
        const defaultFilterData = <DefaultFilterData>{};
        // 遍历columns：获取默认筛选值、排序；设置默认筛选值、排序
        const columns = map(tableConfig?.columns, (column, columnIndex: number) => {
            const {
                dataIndex,
                filters, filterDropdown, filteredValue, defaultFilteredValue,
                // filterMultiple = true, // 筛选
                sorter, sortOrder, defaultSortOrder // 排序
            } = column;
            const defaultKey = getColumnPos(columnIndex);
            const columnKey = getColumnKey(column, defaultKey);
            // 1. 判断是否有筛选项默认值
            if (filters || filterDropdown) {
                const queryParamValue = getQueryParamValue(columnKey);
                if ('filteredValue' in column || 'defaultFilteredValue' in column) {
                    defaultFilterData[columnKey] = filteredValue ?? defaultFilteredValue;
                }
                else if (queryParamValue) {
                    // const paramValue = filterMultiple && isString(queryParamValue)
                    //     ? [queryParamValue] : queryParamValue;
                    // 对于单选多选都使用数组，不然onTableChange时filter数据会丢失
                    const paramValue = isString(queryParamValue) ? [queryParamValue] : queryParamValue;
                    defaultFilterData[columnKey] = paramValue;
                    column.defaultFilteredValue = paramValue;
                }
            }
            // 2. 判断是否有排序默认值
            if (sorter) {
                const orderParamValue = getQueryParamValue('order');
                const orderByParamValue = getQueryParamValue('orderBy');
                // 与排序不同，这里的key是dataIndex
                if ('sortOrder' in column || 'defaultSortOrder' in column) {
                    const order = sortOrder ?? defaultSortOrder;
                    defaultFilterData.order = TABLE_ORDER_MAP[order];
                    defaultFilterData.orderBy = dataIndex;
                }
                else if (orderByParamValue === dataIndex && orderParamValue) {
                    defaultFilterData.order = orderParamValue;
                    defaultFilterData.orderBy = dataIndex;
                    column.defaultSortOrder = TABLE_ORDER_MAP[orderParamValue];
                }
            }
            return column;
        });
        // 遍历controls：获取默认搜索值等；设置默认搜索值等
        const controls = map(defaultFilterConfig?.controls, control => {
            const {name, controlProps} = control;
            if (!name) {
                return control;
            }
            const {value, checked, defaultValue, defaultChecked} = controlProps || {};
            const queryParamValue = getQueryParamValue(name);
            if (
                'value' in controlProps
                || 'defaultValue' in controlProps
                || 'checked' in controlProps
                || 'defaultChecked' in controlProps
            ) {
                defaultFilterData[name] = value ?? checked ?? defaultValue ?? defaultChecked;
            }
            else if (queryParamValue) {
                defaultFilterData[name] = queryParamValue;
                if (!control.controlProps) {
                    control.controlProps = {};
                }
                control.controlProps.defaultValue = queryParamValue;
                control.controlProps.defaultChecked = queryParamValue === 'true'; // 转成Boolean
            }
            return control;

        });
        return {
            columns,
            filterConfig: {
                ...defaultFilterConfig,
                controls
            },
            defaultFilterData
        };
    }, [tableConfig?.columns, defaultFilterConfig, getQueryParamValue]);

    const action = useMemo(() => (
        isString(fetchListAction) ? get(actions, fetchListAction) : fetchListAction
    ), [fetchListAction, actions]);

    const defaultListParams = {
        pageNo: Number(getQueryParamValue('pageNo') || defaultPageNo || DEFAULT_PAGE_NO),
        pageSize: Number(getQueryParamValue('pageSize') || defaultPageSize || DEFAULT_PAGE_SIZE),
        // 若url中参数与filter中参数重复，则以filter中参数为准
        ...getCustomQueryParamsValue(),
        ...defaultFilterData
    };

    const {loading, data, reload, listParams, setListParams} = useTableFetch({
        action,
        defaultListParams,
        errorToast,
        $onRequest,
        $onResponse,
        $onError,
        abort,
        interval
    });

    const [filterData, setFilterData] = useState(defaultFilterData);

    const {
        result: dataSource,
        totalCount,
        httpStatusCode
    } = data || {} as any;

    const pageNo = (data as any)?.pageNo || listParams?.pageNo;
    const pageSize = (data as any)?.pageSize || listParams?.pageSize;

    const loadList = useCallback((extra?: Object) => {
        // 清空筛选
        setSelectedRow({selectedRowKeys: [], selectedItems: []});
        const triggerInfo = {
            triggerBy: FETCH_TRIGGER_BY.Reload,
            triggerByType: FETCH_TRIGGER_BY_RELOAD_TYPE.Manually
        };
        reload({extra: merge(triggerInfo, extra)});
    }, [reload]);

    const handleQueryParamsChange = useCallback(() => {
        const searchParamsString = searchParamsInstance.toString();
        if ($onQueryParamsChange) {
            const searchParams = getSearchParam() as Record<string, string>;
            return $onQueryParamsChange(searchParams, searchParamsString);
        }
        const url = getUrl(location, searchParamsString);
        history.push ? history.push(url) : history(url);
    }, [history, location, searchParamsInstance, getSearchParam, $onQueryParamsChange]);

    const updateQueryParams = useCallback(queryParams => {
        if (isEmpty(queryParamKeys)) {
            return;
        }
        forIn(queryParams, (paramValue, paramKey) => {
            if (!queryParamKeys.includes(paramKey)) {
                return;
            }
            switch (true) {
                case isNumber(paramValue):
                case isString(paramValue):
                    searchParamsInstance.set(paramKey, `${paramValue}`);
                    break;
                case isArray(paramValue):
                    searchParamsInstance.delete(paramKey);
                    paramValue.map(paramValueItem => {
                        searchParamsInstance.append(paramKey, paramValueItem);
                    });
                    break;
                case isNil(paramValue):
                    searchParamsInstance.delete(paramKey);
                    break;
                default:
                    break;
            }
        });
        handleQueryParamsChange();
    }, [queryParamKeys, searchParamsInstance, handleQueryParamsChange]);

    const updateListParams = useCallback((params, extra?) => {
        // 清空筛选
        setSelectedRow({selectedRowKeys: [], selectedItems: []});
        setListParams((prevParams: any) => {
            const newParams = {...prevParams, ...params};
            updateQueryParams(newParams);
            return newParams;
        }, extra);
    }, [setListParams, updateQueryParams]);

    const updateFilterData = useCallback(params => {
        setFilterData((prevFilterData: any) => ({...prevFilterData, ...params}));
    }, []);

    const loadFirstPage = useCallback(() => {
        pageNo !== 1
            ? updateListParams({pageNo: 1}, {triggerBy: FETCH_TRIGGER_BY.Pagination})
            : loadList();
    }, [pageNo, updateListParams, loadList]);

    const handleTableChange = useCallback((pagination, filters, sorter, {action} = {}) => {
        const {current: nextCurrent, pageSize: nextPageSize} = pagination;
        const isPaginationChange = action === 'paginate';
        // 这里处理 filters 和 sorter 的变化
        const {order, field} = sorter;
        const filterData = {
            ...(order
                ? {order: TABLE_ORDER_MAP[order], orderBy: field}
                : (action === 'sort' ? {order: undefined, orderBy: undefined} : {})
            ),
            ...filters
        };
        const params = assign(
            isPaginationChange ? {pageNo: nextCurrent, pageSize: nextPageSize} : {pageNo: 1},
            filterData
        );

        const extra = action === 'paginate'
            ? {triggerBy: FETCH_TRIGGER_BY.Pagination}
            : {triggerBy: FETCH_TRIGGER_BY.Filter};
        updateListParams(params, extra);
        updateFilterData(filterData);
    }, [updateListParams, updateFilterData]);

    const handlePageNoChange = useCallback(pageNo => {
        updateListParams({pageNo}, {triggerBy: FETCH_TRIGGER_BY.Pagination});
    }, [updateListParams]);

    const handlePageSizeChange = useCallback((_pageNo, pageSize) => {
        updateListParams({pageSize}, {triggerBy: FETCH_TRIGGER_BY.Pagination});
    }, [updateListParams]);

    const handleFilterChange = useCallback(params => {
        const newParams = {pageNo: 1, ...params};
        updateListParams(newParams, {triggerBy: FETCH_TRIGGER_BY.Filter});
        updateFilterData(params);
        // 当参数值没有变化时，强制重新加载
        const needReload = every(newParams, (value, key) => value === listParams[key]);
        needReload && reload({extra: {triggerBy: FETCH_TRIGGER_BY.Filter}});
    }, [listParams, updateListParams, updateFilterData, reload]);

    const handleSelectedRowKeysSet = useCallback(selectedRowKeys => {
        const selectedItems = map(selectedRowKeys, key => (
            rowKey ? find(dataSource, {[rowKey]: key}) : get(dataSource, key)
        )) as never[];
        setSelectedRow({selectedRowKeys, selectedItems});
    }, [dataSource, rowKey]);

    const handleSelectChange = useCallback((selectedRowKeys: never[]) => {
        handleSelectedRowKeysSet(selectedRowKeys);
        customFunctions?.handleSelectChange?.(selectedRowKeys);
    }, [handleSelectedRowKeysSet, customFunctions]);

    const tableActions = useMemo(() => ({
        loadList,
        loadFirstPage,
        setListParams,
        handleFilterChange,
        // checkAll
        ...customFunctions
    }), [
        loadList,
        loadFirstPage,
        setListParams,
        handleFilterChange,
        customFunctions
    ]);

    Object.assign(callbackRef.current.tableActions, tableActions);
    callbackRef.current.tableOptions = tableOptions;

    const {selectedRowKeys} = selectedRow;

    useEffect(() => {
        // 解决业务侧history back、push等操作时，列表数据不刷新的问题
        if (!isEmpty(queryParamKeys)) {
            let needReload = false;
            forIn(defaultListParams, (paramValue, paramKey) => {
                if (!isEqual(paramValue, listParams[paramKey])) {
                    needReload = true;
                    return false;
                }
            });
            if (!needReload) {
                forIn(listParams, (paramValue, paramKey) => {
                    if (!isEqual(paramValue, defaultListParams[paramKey])) {
                        needReload = true;
                        return false;
                    }
                });
            }
            needReload && setListParams(defaultListParams);
        }
    }, [search]);

    return {
        key: search,
        className,
        title,
        toolbarProps: {
            withToolbar: Boolean(bulkConfig || bulkComp || filterConfig?.controls?.length || filterComp),
            bulkProps: {
                bulkComp,
                bulkConfig
            },
            filterProps: {
                filterComp,
                filterConfig
            },
            onSearch: updateListParams,
            onReload: loadList
        },
        tableProps: {
            rowKey: rowKey || ((__: any, index: number) => index),
            loading,
            dataSource,
            ...(select !== 'none' ? {
                rowSelection: {
                    selectedRowKeys,
                    type: select === 'single' ? 'radio' : 'checkbox',
                    getCheckboxProps: get(tableConfig, 'getCheckboxProps'),
                    onChange: handleSelectChange
                }
            } : {}),
            pagination: {
                current: pageNo,
                pageSize,
                pageSizeOptions: pageSizeOptions || DEFAULT_PAGESIZE_OPTIONS,
                total: totalCount,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: () => `${locale?.total}${totalCount}${locale?.stripData}`,
                ...(card ? {
                    onChange: handlePageNoChange,
                    onShowSizeChange: handlePageSizeChange
                } : {})
            },
            onChange: handleTableChange,
            ...tableConfig,
            columns
        },
        tableComp,
        card,
        emptyConfig,
        emptyComp,
        dialogProps,
        tableActions,
        filterData,
        listParams,
        httpStatusCode,
        setListParams,
        setSelectedRowKeys: handleSelectedRowKeysSet,
        updateListParams,
        handleCommand,
        handleTableCommand,
        handleFilterChange
    };
};


export default useTable;
